// components/SystemHealthWidget.tsx
// Real-time system health monitoring widget

import React, { useState, useEffect } from 'react';
import { getSystemHealth } from '../services/analytics';
import type { SystemHealth } from '../types';
import './SystemHealthWidget.css';

export const SystemHealthWidget: React.FC = () => {
    const [health, setHealth] = useState<SystemHealth | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadSystemHealth();
        const interval = setInterval(loadSystemHealth, 60000); // Refresh every minute
        return () => clearInterval(interval);
    }, []);

    const loadSystemHealth = async () => {
        try {
            const data = await getSystemHealth();
            setHealth(data);
        } catch (error) {
            console.error('Error loading system health:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="system-health-widget">
                <h3>System Health</h3>
                <div className="loading-state">Loading...</div>
            </div>
        );
    }

    if (!health) {
        return (
            <div className="system-health-widget">
                <h3>System Health</h3>
                <div className="empty-state">No health data available</div>
            </div>
        );
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'operational':
                return 'green';
            case 'degraded':
                return 'yellow';
            case 'down':
                return 'red';
            default:
                return 'gray';
        }
    };

    return (
        <div className="system-health-widget">
            <h3>🏥 System Health</h3>

            <div className="health-status">
                <div className="status-item">
                    <span className="status-label">Firebase:</span>
                    <span className={`status-badge ${getStatusColor(health.firebase.status)}`}>
                        {health.firebase.status}
                    </span>
                    <span className="status-detail">
                        {health.firebase.responseTime}ms
                    </span>
                </div>

                <div className="status-item">
                    <span className="status-label">Error Rate:</span>
                    <span className={health.errorRate > 50 ? 'status-badge red' : 'status-badge green'}>
                        {health.errorRate}/hr
                    </span>
                </div>

                <div className="status-item">
                    <span className="status-label">Crash Rate:</span>
                    <span className={health.crashRate > 10 ? 'status-badge red' : 'status-badge green'}>
                        {health.crashRate}/hr
                    </span>
                </div>
            </div>

            <div className="last-updated">
                Last updated: {health.timestamp.toLocaleString()}
            </div>
        </div>
    );
};

export default SystemHealthWidget;
